import { SelectField } from '@/components';
import { useFiscalYearOptions } from '@/features/search/_hooks/budget-types';
import type { ProjectSearchFormType } from '@/features/search/_models/projects-search';
import type { AmountSearchConditionFieldName } from '@/features/search/_utils/project-amount-search';
import { type FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = {
  name:
    | AmountSearchConditionFieldName<'baseAmountYear'>
    | AmountSearchConditionFieldName<'targetAmountYear'>;
  type: 'baseAmountYear' | 'targetAmountYear';
  index: number;
};

export const AmountSearchFiscalYearField: FC<Props> = ({
  name,
  index,
  type,
}) => {
  const { t } = useTranslation();
  const { control, setValue, getValues } =
    useFormContext<ProjectSearchFormType>();

  const fiscalYearOptions = useFiscalYearOptions();

  const handleChange = useCallback(
    (value: string | null) => {
      // 年度が削除された場合（null または undefined）
      if (!value) {
        const conditionType = getValues(
          `formulaConditions.${index}.conditionType`,
        );

        // baseAmountYearが削除された場合
        if (name.includes('baseAmountYear')) {
          // 共通: 予算種別をクリア
          setValue(`formulaConditions.${index}.baseAmountType`, null);

          if (conditionType === 'threshold') {
            // 閾値の場合
            setValue(`formulaConditions.${index}.baseAmountValueFrom`, null);
            setValue(`formulaConditions.${index}.baseAmountValueTo`, null);
            setValue(`formulaConditions.${index}.baseAmountValueIsnull`, false);
          } else if (conditionType === 'trend') {
            // 増減の場合
            setValue(`formulaConditions.${index}.trendConditionValue`, null);
            setValue(`formulaConditions.${index}.trendCondition`, null);
          }
        }

        if (type === 'targetAmountYear') {
          setValue(`formulaConditions.${index}.targetAmountType`, null);
          setValue(`formulaConditions.${index}.trendConditionValue`, null);
          setValue(`formulaConditions.${index}.trendCondition`, null);
        }
      }
    },
    [index, name, type, setValue, getValues],
  );

  return (
    <SelectField
      name={name}
      label={t('search-year')}
      control={control}
      errors={{}}
      wrapperStyle={{ marginBottom: 0 }}
      width={120}
      loading={fiscalYearOptions.isPending}
      options={fiscalYearOptions.data || []}
      onChange={handleChange}
      shouldCoexistOnChange
    />
  );
};
